<!--
  Una aplicación abierta por su enlace público.

  No pasa por la sesión del panel: quien entra aquí es alguien invitado a esta
  aplicación, con su propia cuenta (ver `loginFor` en `shared/people.ts`), o
  cualquiera si la aplicación está abierta.
-->
<script lang="ts">
  import { appPath } from "@shared/appPaths";
  import { personDisplayName } from "@shared/people";
  import type { AppBundle } from "@shared/types";

  import AppView from "../components/AppView.svelte";
  import Icon from "../components/Icon.svelte";
  import MemberLogin from "../components/MemberLogin.svelte";
  import PublishedError from "../components/PublishedError.svelte";
  import { Dropdown, Loading, MenuItem, MenuSeparator } from "../components/ui";
  import { cx } from "../lib/cx";
  import { publicPageLoader } from "../lib/htmlDocs";
  import { errorMessage, jsonBody, pbApp } from "../lib/pb";
  import { publishedPeople, setPeople } from "../lib/people.svelte";
  import { navigate, type RouteParams } from "../lib/router.svelte";

  type Bundle = AppBundle & { requiresAuth?: boolean };

  let { params }: { params: RouteParams } = $props();

  const slug = $derived(params.slug ?? "");
  const pageSlug = $derived(params.pageSlug);

  let bundle = $state<Bundle | null>(null);
  let error = $state("");
  let loading = $state(true);
  /** Sube al entrar o al salir: es lo que hace releer al padrón de la app. */
  let session = $state(0);
  /** Quien visita una aplicación pública pidió iniciar sesión. */
  let signingIn = $state(false);

  const people = publishedPeople(
    () => slug,
    () => {
      void session;
      return pbApp.authStore.isValid;
    },
  );
  setPeople(people);

  /*
   * Quien entró a esta aplicación (la colección `members`, no la sesión del
   * panel). `cuenta` es el correo real -- `email` se deja vacío a propósito,
   * ver `server/bootstrap.ts` -- y `login` es solo lo que se compara al
   * entrar, no algo que mostrar.
   */
  const member = $derived.by(() => {
    void session;
    if (!pbApp.authStore.isValid) return null;
    return pbApp.authStore.record as { id?: string; name?: string; cuenta?: string } | null;
  });
  /*
   * Obtiene el nombre de la fila de personas antes de recurrir a la cuenta. El name de la
   * cuenta puede ser el correo sin dominio, como una cédula, y no el nombre real. Véase
   * personDisplayName.
   */
  const memberName = $derived.by(() => {
    const mine = people.list.find((p) => p.id === member?.id) ?? null;
    return (mine ? personDisplayName(mine) : "") || member?.name || member?.cuenta || "";
  });
  const memberInitials = $derived.by(() => {
    const parts = memberName.split(/[\s@.]+/).filter(Boolean);
    return (
      parts
        .slice(0, 2)
        .map((w) => w[0]?.toUpperCase() ?? "")
        .join("") || "?"
    );
  });

  /**
   * Lo que cabe en la barra de un teléfono: la primera palabra del nombre. Un
   * nombre que es en realidad un correo se corta en la arroba.
   */
  const memberFirstName = $derived(memberName.split(/[\s@]+/).find(Boolean) ?? "");

  /*
   * Conserva la función de carga mientras no cambie el enlace de la aplicación. HtmlFrame
   * recarga el documento si recibe otra función; recrearla en cada actualización
   * reiniciaría la página y podría perder los mensajes del tema.
   */
  const makePageLoader = $derived((pageId: string) => publicPageLoader(slug, pageId));

  let loadTurn = 0;

  /** Carga el paquete de la aplicación publicada, con la sesión de invitado si la hay. */
  async function load() {
    // Salir de la sesión invalida cualquier respuesta que se pidió con ella.
    const mine = ++loadTurn;
    loading = true;
    try {
      const headers: Record<string, string> = {};
      if (pbApp.authStore.token) headers.authorization = pbApp.authStore.token;
      const res = await fetch(`/api/public/${encodeURIComponent(slug)}`, { headers });
      const data = jsonBody(await res.text());
      if (!res.ok || !data) throw new Error(data?.error ?? "No se pudo abrir la aplicación");
      if (mine !== loadTurn) return;
      bundle = data as Bundle;
      error = "";
    } catch (err) {
      if (mine === loadTurn) error = errorMessage(err);
    } finally {
      if (mine === loadTurn) loading = false;
    }
  }

  $effect(() => {
    void slug;
    void load();
  });

  /** Cierra la sesión del invitado y recarga la página. */
  function logOut() {
    pbApp.authStore.clear();
    window.location.reload();
  }

  /** Recarga la aplicación después de iniciar sesión. */
  async function signedIn() {
    signingIn = false;
    session += 1;
    await load();
  }

  /** Cierra la sesión del invitado y recarga la aplicación. */
  async function signOut() {
    pbApp.authStore.clear();
    bundle = null;
    session += 1;
    await load();
  }
</script>

{#if loading && !bundle}
  <Loading label="Abriendo" />
{:else if error}
  <PublishedError message={error} onLogout={logOut} />
{:else if bundle?.requiresAuth}
  <MemberLogin {bundle} onSignedIn={signedIn} />
{:else if bundle && signingIn}
  <MemberLogin {bundle} onSignedIn={signedIn} onCancel={() => (signingIn = false)} />
{:else if bundle}
  <AppView
    {bundle}
    client={pbApp}
    {makePageLoader}
    {pageSlug}
    onOpenPage={(p) => navigate(appPath(slug, p.isHome ? undefined : p.slug))}
  >
    <!--
      El pie usa tres piezas del catálogo tal cual: `.avatar` para las
      iniciales, `.identity` para el nombre y la cuenta, y `.btn-icon.sm` con
      `.btn-danger-quiet` para cerrar sesión --que solo avisa en rojo cuando el
      cursor ya está encima--.
    -->
    {#snippet footer()}
      {#if member}
        <span class="member-avatar avatar">{memberInitials}</span>
        <div class="member-meta identity">
          <strong>{memberName}</strong>
          {#if member.cuenta && member.cuenta !== memberName}
            <span>{member.cuenta}</span>
          {/if}
        </div>
        <button
          type="button"
          onclick={signOut}
          aria-label="Cerrar la sesión"
          class="btn-sign-out btn-icon sm btn-danger-quiet"
        >
          <Icon name="log-out" size={18} />
        </button>
      {:else if bundle?.hiddenPages}
        <!--
          Una aplicación pública con páginas que se abren con un rol: sin esto,
          quien fue invitado no tenía por dónde entrar a verlas.
        -->
        <button
          type="button"
          onclick={() => (signingIn = true)}
          class="btn-open-member-login btn sm w-full"
        >
          <Icon name="login-01" size={16} /> Iniciar sesión
        </button>
      {/if}
    {/snippet}

    <!--
      En un teléfono no hay pie: quien entró va en la barra de arriba, con sus
      iniciales y su primer nombre, y lo demás --nombre entero, cuenta y salir--
      en un menú.
    -->
    {#snippet account()}
      {#if member}
        <Dropdown align="right" class="menu-member-account">
          {#snippet trigger({ open, toggle })}
            <button
              type="button"
              onclick={toggle}
              aria-expanded={open}
              aria-label={`Cuenta de ${memberName}`}
              class={cx("btn-open-member-menu", "btn btn-ghost sm", open && "active")}
            >
              <span class="member-avatar-sm avatar">{memberInitials}</span>
              <span class="member-first-name">{memberFirstName}</span>
            </button>
          {/snippet}
          {#snippet children(close)}
            <div class="member-menu-head identity">
              <strong>{memberName}</strong>
              {#if member.cuenta && member.cuenta !== memberName}
                <span>{member.cuenta}</span>
              {/if}
            </div>
            <MenuSeparator />
            <MenuItem
              danger
              onclick={() => {
                close();
                void signOut();
              }}
            >
              {#snippet icon()}<Icon name="log-out" size={16} />{/snippet}
              Cerrar sesión
            </MenuItem>
          {/snippet}
        </Dropdown>
      {:else if bundle?.hiddenPages}
        <button
          type="button"
          onclick={() => (signingIn = true)}
          class="btn-open-member-login-bar btn btn-ghost sm"
        >
          <Icon name="login-01" size={16} /> Entrar
        </button>
      {/if}
    {/snippet}
  </AppView>
{/if}

<style>
  /* La barra de un teléfono es baja: el avatar se achica a la altura del botón. */
  .member-avatar-sm {
    width: 1.5rem;
    height: 1.5rem;
    font-size: var(--text-xs);
  }

  .btn-open-member-menu {
    gap: var(--sp-6);
    padding-inline: var(--sp-4) var(--sp-8);
  }

  .member-first-name {
    max-width: 7rem;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .member-menu-head {
    padding: var(--sp-8) var(--sp-10);
  }
</style>
